import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, TrendingUp } from "lucide-react";
import { MaskLines } from "../lib/motion";
import { IMG, BRAND } from "../data/site";
import { HeroNotifications } from "./HeroNotifications";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden grain">
      {/* Background image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Twórczyni PMPOINT w apartamencie z widokiem na nocną Warszawę"
          className="w-full h-full object-cover hero-live"
          style={{ objectPosition: "72% center" }}
          fetchPriority="high"
          width="1600"
          height="900"
        />
      </motion.div>
      <div className="absolute inset-0 photo-overlay" />
      <div className="absolute inset-0 bg-gradient-to-t from-graphite via-transparent to-graphite/40" />

      {/* Live notification stream from the phone */}
      <HeroNotifications />

      {/* Floating earning chips near the model */}
      <div className="hidden lg:block absolute inset-0 z-20 pointer-events-none">
        {[
          { amt: "+23 000 zł", sub: "w 30 dni", top: "20%", right: "30%", delay: 1.3, dur: 5 },
          { amt: "+9 000 zł", sub: "pierwszy tydzień", top: "46%", right: "8%", delay: 1.7, dur: 6 },
          { amt: "+15 400 zł", sub: "nowy profil", top: "68%", right: "26%", delay: 2.1, dur: 5.5 },
        ].map((c, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{ top: c.top, right: c.right }}
            initial={{ opacity: 0, scale: 0.7, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: c.delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: c.dur, ease: "easeInOut" }}
              className="glass rounded-xl pl-3 pr-4 py-2.5 flex items-center gap-3 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-coral/20 text-coral">
                <TrendingUp size={16} strokeWidth={2.4} />
              </span>
              <span className="leading-tight">
                <span className="block font-display font-bold text-gold text-lg leading-none">{c.amt}</span>
                <span className="block text-[10px] uppercase tracking-[0.12em] text-white/55 mt-0.5">{c.sub}</span>
              </span>
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Content */}
      <motion.div style={{ y: textY }} className="relative z-10 container-pm min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="eyebrow mb-5"
          data-testid="hero-eyebrow"
        >
          Rekrutujemy twórczynie 18+
        </motion.p>

        <h1 className="font-display font-semibold text-white tracking-[-0.045em] leading-[0.96] text-[clamp(48px,8vw,92px)] max-w-[16ch]">
          <MaskLines
            lines={["Zarabiaj na", <>swoich <span className="text-coral">zasadach</span></>]}
            startDelay={0.25}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-7 max-w-[46ch] text-base sm:text-lg text-white/80"
          data-testid="hero-lead"
        >
          PMPOINT to marketing i technologia dla ambitnych twórczyń. Dajemy Ci strategię,
          narzędzia i wsparcie, by zamieniać uwagę w realne przychody.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href="#aplikuj"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 h-[52px] px-7 rounded-lg bg-coral text-white font-bold hover:bg-coral-hover hover:-translate-y-0.5 transition-all"
          >
            Sprawdź model współpracy
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 h-[52px] px-7 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#1EBE5D] hover:-translate-y-0.5 transition-all"
          >
            <MessageCircle size={18} />
            Porozmawiaj poufnie
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">Przewiń</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-px h-8 bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};
